import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LibraryModelVersion, Tag } from '~/features/x-ray/library/types';
import { getSortedCategoryModels } from '~/features/x-ray/library/utils';

import { SliceType } from '../types';
import { LibraryState as State } from './types';

export const initialState: State = {
  adminUpdateModel: null,
  cartStatusMessage: null,
  categoryModels: new Map(),
  enableCarousel: true,
  isCartVisible: false,
  modelPreview: null,
  modelsInCart: [],
  filters: {
    globalFilters: [],
    categoryFilters: {},
  },
  query: {
    globalQuery: [],
    categoryQuery: {},
  },
  selectedCategory: null,
  step: 'model-library',
};

export default createSlice({
  name: SliceType.XRayLibrary,
  initialState,
  selectors: {
    selectAdminUpdateModel: (state) => state.adminUpdateModel,
    selectCarousel: (state) => state.enableCarousel,
    selectCart: (state) => state.modelsInCart,
    selectCartStatusMessage: (state) => state.cartStatusMessage,
    selectCategory: (state) => state.selectedCategory,
    selectCategoryModels: (state) =>
      getSortedCategoryModels(state.modelsInCart),
    selectFilters: (state) => state.filters,
    selectIsCartVisible: (state) => state.isCartVisible,
    selectModelPreview: (state) => state.modelPreview,
    selectQuery: (state) => state.query,
    selectStep: (state) => state.step,
  },
  reducers: {
    addAllModelsToCart: (
      state,
      action: PayloadAction<{
        models: LibraryModelVersion[];
        category: Tag;
        statusMessage?: State['cartStatusMessage'];
      }>,
    ) => {
      // Add all models that are not already in the cart or are in a different category
      const modelsToAdd = action.payload.models.filter(
        (model) =>
          !state.modelsInCart.find(
            (m) => model.libraryModelId === m.libraryModelId,
          ) ||
          state.modelsInCart.find(
            (m) =>
              model.libraryModelId === m.libraryModelId &&
              m.tags[0]?.label !== action.payload.category.label,
          ),
      );
      if (action.payload.statusMessage) {
        state.cartStatusMessage = action.payload.statusMessage;
      }
      if (state.modelsInCart.length === 0) {
        // Automatically expand the shopping cart when adding models for the first time
        state.isCartVisible = true;
      }
      state.modelsInCart = [
        ...state.modelsInCart.filter(
          (model) =>
            !modelsToAdd.find((m) => m.libraryModelId === model.libraryModelId),
        ),
        ...modelsToAdd.map((model) => ({
          ...model,
          tags: [action.payload.category],
        })),
      ];
    },
    addModelToCart: (
      state,
      action: PayloadAction<{
        model: LibraryModelVersion;
        category: Tag;
        statusMessage?: State['cartStatusMessage'];
      }>,
    ) => {
      const existingModel = state.modelsInCart.find(
        (model) => model.libraryModelId === action.payload.model.libraryModelId,
      );
      if (existingModel) {
        if (existingModel.tags[0]?.label === action.payload.category.label) {
          return;
        } else {
          // Remove it from the previous category before adding it to the new one
          state.modelsInCart = state.modelsInCart.filter(
            (model) => model.libraryModelId !== existingModel.libraryModelId,
          );
        }
      }
      if (action.payload.statusMessage) {
        state.cartStatusMessage = action.payload.statusMessage;
      }
      if (state.modelsInCart.length === 0) {
        // Automatically expand the shopping cart when adding a model for the first time
        state.isCartVisible = true;
      }
      state.modelsInCart = [
        ...state.modelsInCart,
        { ...action.payload.model, tags: [action.payload.category] },
      ];
    },
    clearCart: (state) => {
      state.modelsInCart = [];
      state.cartStatusMessage = null;
      state.isCartVisible = false;
    },
    clearCartStatusMessage: (state) => {
      state.cartStatusMessage = null;
    },
    removeModelFromCart: (
      state,
      action: PayloadAction<{
        model: LibraryModelVersion;
        statusMessage?: State['cartStatusMessage'];
      }>,
    ) => {
      const index = state.modelsInCart.findIndex(
        (model) => model.libraryModelId === action.payload.model.libraryModelId,
      );
      if (index === -1) {
        return;
      }
      if (action.payload.statusMessage) {
        state.cartStatusMessage = action.payload.statusMessage;
      }
      state.modelsInCart.splice(index, 1);
    },
    setAdminUpdateModel: (
      state,
      action: PayloadAction<State['adminUpdateModel']>,
    ) => {
      state.adminUpdateModel = action.payload;
    },
    setCategory: (state, action: PayloadAction<State['selectedCategory']>) => {
      state.selectedCategory = action.payload;
    },
    setFilters: (state, action: PayloadAction<State['filters']>) => {
      state.filters = action.payload;
    },
    setModelPreview: (state, action: PayloadAction<State['modelPreview']>) => {
      state.modelPreview = action.payload;
    },
    setCarousel: (state, action: PayloadAction<boolean>) => {
      state.enableCarousel = action.payload;
    },
    setQuery: (state, action: PayloadAction<State['query']>) => {
      state.query = action.payload;
    },
    setStep: (state, action: PayloadAction<State['step']>) => {
      state.step = action.payload;
    },
    toggleIsCartVisible: (state) => {
      state.isCartVisible = !state.isCartVisible;
    },
  },
});
